import React, { useEffect, useRef, useState } from 'react';
import { VehicleLocation, getVehicleLocations } from '../data/dashboardData';
import './VehicleTrackerMap.css';

declare global {
  interface Window {
    google: any;
  }
}

interface VehicleTrackerMapProps {
  center?: { lat: number; lng: number };
  zoom?: number;
}

const DEFAULT_CENTER = { lat: 50.9416, lng: -2.6321 }; // Yeovil coordinates

// Cached so concurrent callers / re-mounts share a single script tag and promise
let googleMapsPromise: Promise<void> | null = null;

// Helper function to load Google Maps API
const loadGoogleMapsAPI = (apiKey: string): Promise<void> => {
  if (window.google && window.google.maps) {
    return Promise.resolve();
  }

  if (!googleMapsPromise) {
    googleMapsPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
      script.async = true;
      script.defer = true;
      script.onerror = () => {
        googleMapsPromise = null; // allow a retry on the next mount
        reject(new Error('Failed to load Google Maps API'));
      };
      script.onload = () => {
        resolve();
      };
      document.head.appendChild(script);
    });
  }

  return googleMapsPromise;
};

const VehicleTrackerMap: React.FC<VehicleTrackerMapProps> = ({
  center = DEFAULT_CENTER,
  zoom = 10
}) => {
  const [vehicles, setVehicles] = useState<VehicleLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [map, setMap] = useState<any>(null);
  const [vehicleTypeCounts, setVehicleTypeCounts] = useState<{ type: string; color: string; count: number }[]>([]);

  const mapElementRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);

  // Depend on the coordinates themselves, not the object identity - the parent
  // passes an inline literal, so `center` is a new object on every render.
  const { lat, lng } = center;

  useEffect(() => {
    let cancelled = false;

    const initializeMap = async () => {
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

      if (!apiKey) {
        setError('Google Maps API key not configured');
        setLoading(false);
        return;
      }

      try {
        await loadGoogleMapsAPI(apiKey);
      } catch (err) {
        console.error('Failed to load Google Maps API:', err);
        if (!cancelled) {
          setError('Failed to load map service');
          setLoading(false);
        }
        return;
      }

      if (cancelled) {
        return;
      }

      setMapsLoaded(true);

      const fetchVehicles = async () => {
        try {
          setLoading(true);
          const vehicleData = await getVehicleLocations();
          if (cancelled) {
            return;
          }
          setVehicles(vehicleData);

          // Calculate vehicle type counts grouped by workTypeGroup
          const countMap = new Map<string, { type: string; color: string; count: number }>();
          vehicleData.forEach((vehicle) => {
            const type = vehicle.workTypeGroup || 'Unassigned';
            const color = vehicle.hexColor || '#808080';
            const key = `${type}-${color}`;

            if (countMap.has(key)) {
              countMap.get(key)!.count++;
            } else {
              countMap.set(key, { type, color, count: 1 });
            }
          });

          // Convert to array and sort by type name
          const countsArray = Array.from(countMap.values()).sort((a, b) => a.type.localeCompare(b.type));
          setVehicleTypeCounts(countsArray);

          setError(null);
        } catch (err: any) {
          console.error('Error fetching vehicle locations:', err);
          if (cancelled) {
            return;
          }
          const errorMessage = err?.response?.status === 404
            ? 'Vehicle locations endpoint not found'
            : err?.message || 'Failed to load vehicle locations';
          setError(errorMessage);
          setVehicles([]);
          setVehicleTypeCounts([]);
        } finally {
          if (!cancelled) {
            setLoading(false);
          }
        }
      };

      fetchVehicles();
    };

    initializeMap();

    return () => {
      cancelled = true;
    };
  }, []);

  // Create the map exactly once, and only once the container has real dimensions.
  useEffect(() => {
    if (!mapsLoaded || mapInstanceRef.current) {
      return;
    }

    const mapElement = mapElementRef.current;
    if (!mapElement) {
      return;
    }

    let resizeObserver: ResizeObserver | null = null;

    const createMap = () => {
      if (mapInstanceRef.current || !mapElementRef.current) {
        return;
      }

      console.log('Initializing map with dimensions:', {
        width: mapElement.offsetWidth,
        height: mapElement.offsetHeight
      });

      const newMap = new window.google.maps.Map(mapElement, {
        center: { lat, lng },
        zoom,
        mapTypeControl: true,
        fullscreenControl: true,
        streetViewControl: false,
      });

      mapInstanceRef.current = newMap;
      setMap(newMap);
    };

    if (mapElement.offsetWidth === 0 || mapElement.offsetHeight === 0) {
      // Container not laid out yet - wait for it rather than giving up.
      resizeObserver = new ResizeObserver(() => {
        if (mapElement.offsetWidth > 0 && mapElement.offsetHeight > 0) {
          resizeObserver?.disconnect();
          resizeObserver = null;
          createMap();
        }
      });
      resizeObserver.observe(mapElement);
    } else {
      createMap();
    }

    return () => {
      resizeObserver?.disconnect();
    };
    // lat/lng/zoom are only the *initial* view; later changes are applied below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapsLoaded]);

  // Apply centre/zoom changes to the existing map instead of rebuilding it.
  useEffect(() => {
    if (!map) {
      return;
    }
    map.setCenter({ lat, lng });
    map.setZoom(zoom);
  }, [map, lat, lng, zoom]);

  // Tear down markers and the map instance on unmount.
  useEffect(() => {
    return () => {
      markersRef.current.forEach((marker) => marker.setMap(null));
      markersRef.current = [];

      if (mapInstanceRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(mapInstanceRef.current);
      }
      mapInstanceRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!map || vehicles.length === 0) {
      return;
    }

    // Clear existing markers
    markersRef.current.forEach((marker) => marker.setMap(null));
    const newMarkers: any[] = [];

    // Helper function to get initials from name
    const getInitials = (name: string): string => {
      if (!name) return '?';
      const words = name.trim().split(/\s+/);
      if (words.length === 1) {
        return words[0].substring(0, 2).toUpperCase();
      }
      return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    };

    // Helper function to create custom marker icon with initials
    const createMarkerIcon = (initials: string, color: string) => {
      // Determine text color based on background color
      const isLightColor = color.toLowerCase() === '#ffffff' ||
                           color.toLowerCase() === '#fff' ||
                           color.toLowerCase() === 'white';
      const textColor = isLightColor ? '#000000' : '#FFFFFF';

      const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
          <circle cx="20" cy="20" r="18" fill="${color}" stroke="#FFFFFF" stroke-width="3"/>
          <text x="20" y="20" text-anchor="middle" dy=".35em" 
                font-family="Arial, sans-serif" font-size="12" font-weight="bold" fill="${textColor}">
            ${initials}
          </text>
        </svg>
      `;
      return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
    };

    // Create markers with initials
    vehicles.forEach((vehicle) => {
      const position = {
        lat: vehicle.gpsLatitude,
        lng: vehicle.gpsLongitude,
      };

      const employeeName = vehicle.employeeName?.trim() || vehicle.vehicleRegistration.trim();
      const initials = getInitials(vehicle.employeeName || vehicle.vehicleRegistration);
      const color = vehicle.hexColor || '#FF0000';

      // Create marker with custom icon containing initials
      const marker = new window.google.maps.Marker({
        position,
        map,
        title: employeeName,
        icon: {
          url: createMarkerIcon(initials, color),
          scaledSize: new window.google.maps.Size(40, 40),
          anchor: new window.google.maps.Point(20, 20),
        },
      });

      newMarkers.push(marker);
    });

    markersRef.current = newMarkers;

    // Fit bounds to show all markers
    if (newMarkers.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      newMarkers.forEach((marker) => {
        bounds.extend(marker.getPosition());
      });
      map.fitBounds(bounds, { padding: 50 });
    }
  }, [map, vehicles]);

  if (error) {
    return (
      <div className="vehicle-map-error">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="vehicle-tracker-map-wrapper">
      {loading && (
        <div className="vehicle-map-loading">
          <p>Loading vehicle locations...</p>
        </div>
      )}
      {!loading && vehicleTypeCounts.length > 0 && (
        <div className="vehicle-legend">
          {vehicleTypeCounts.map((item) => (
            <div key={`${item.type}-${item.color}`} className="legend-item">
              <div 
                className="legend-color-box" 
                style={{ backgroundColor: item.color }}
              ></div>
              <span className="legend-label">{item.type}: {item.count}</span>
            </div>
          ))}
        </div>
      )}
      <div id="vehicle-map" className="vehicle-map-container" ref={mapElementRef}></div>
    </div>
  );
};

export default VehicleTrackerMap;
